import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { XCircleIcon, AlertTriangleIcon, ClockIcon, TrendingDownIcon, UsersIcon, DatabaseIcon } from 'lucide-react';
const problems = [{
  icon: ClockIcon,
  title: 'Hours of manual data entry',
  description: 'Your team spends 40% of their time on administrative tasks instead of selling. Every lead is entered manually, every email logged individually.'
}, {
  icon: DatabaseIcon,
  title: 'Scattered customer data without overview',
  description: 'Information is scattered across Excel, emails, notes, and various tools. Nobody has a complete overview of the customer.'
}, {
  icon: TrendingDownIcon,
  title: 'Deals fall through the cracks',
  description: 'Without systematic follow-up, you forget potential customers. Studies show: 80% of deals require 5+ contacts – most give up after 2.'
}, {
  icon: UsersIcon,
  title: 'No transparency in the team',
  description: 'Who is working on which deal? What activities have been carried out? Without real-time insight, you cannot lead your team effectively.'
}, {
  icon: AlertTriangleIcon,
  title: 'Unreliable revenue forecasts',
  description: 'Your pipeline is a guessing game. You cannot predict what revenue will come next month – making planning impossible.'
}, {
  icon: XCircleIcon,
  title: 'Scaling is impossible',
  description: 'Every new employee means chaos. Without standardized processes, you cannot grow without losing quality.'
}];
export function ProblemSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section className="py-24 lg:py-32 bg-[#0a0a0a]" ref={ref} data-id="element-186">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-187">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center max-w-3xl mx-auto mb-16" data-id="element-188">
          <span className="inline-block px-4 py-1.5 rounded-full bg-red-500/10 text-red-400 text-sm font-medium mb-6" data-id="element-189">
            The Problem
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight" data-id="element-190">
            Does this sound familiar?
          </h2>
          <p className="mt-6 text-lg text-gray-400" data-id="element-191">
            Most sales teams struggle with the same problems. The result: lost
            deals, frustrated employees, and stagnant revenue.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-192">
          {problems.map((problem, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} className="p-6 rounded-2xl bg-[#1a1a1a] border border-white/5" data-id="element-193">
              <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mb-4" data-id="element-194">
                <problem.icon className="w-6 h-6 text-red-400" data-id="element-195" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2" data-id="element-196">
                {problem.title}
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed" data-id="element-197">
                {problem.description}
              </p>
            </motion.div>)}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.8
      }} className="mt-16 p-8 rounded-2xl bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/20 text-center" data-id="element-198">
          <p className="text-xl lg:text-2xl text-white font-medium" data-id="element-199">
            <span className="text-red-400" data-id="element-200">The hard truth:</span> Every day
            without an optimized CRM costs you an{' '}
            <span className="text-white font-bold" data-id="element-201">average of 2,847€</span> in
            lost deals.
          </p>
        </motion.div>
      </div>
    </section>;
}